import { ColDef } from 'ag-grid-community';
import { IconProps } from 'components/ui';
import { ChangeEvent, useMemo } from 'react';
import {
  ListingBookmarkTypeEnum,
  ListingTypeEnum,
  NetworkBasic,
  OrgNetworks,
  ProductTypes,
} from './api/generated';

export interface ColumnDef extends ColDef {
  field: string;
  width?: number;
  editable?: boolean;
  order?: number;
}

export interface INetworks {
  networks: OrgNetworks[];
}

export const productType: {
  id: ProductTypes;
  title: string;
  icon: IconProps['component'];
}[] = [
  {
    id: ProductTypes.Ct,
    title: 'Cattle',
    icon: 'CattleIcon',
  },
  {
    id: ProductTypes.Sh,
    title: 'Sheep',
    icon: 'SheepIcon',
  },
  {
    id: ProductTypes.Gt,
    title: 'Goats',
    icon: 'GoatIcon',
  },
];

export const priceType: {
  id: string;
  title: string;
}[] = [
  {
    id: 'PER_HEAD',
    title: 'Per Head',
  },
  {
    id: 'HSCW',
    title: 'HSCW',
  },
  {
    id: 'LIVEWEIGHT',
    title: 'Liveweight',
  },
];
export const inputType = {
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  INPUT: 'input',
  SELECT: 'select',
  MULTISELECT: 'multiselect',
};
export const states: string[] = [
  'ACT',
  'NSW',
  'NT',
  'QLD',
  'SA',
  'TAS',
  'VIC',
  'WA',
];

export const soldStatus: {
  label: string;
  value: string;
}[] = [
  { label: 'Filled', value: 'true' },
  { label: 'Open', value: 'false' },
];

export const types: {
  label: string;
  value: string;
}[] = [
  { label: 'Wanted', value: 'BUY' },
  { label: 'For Sale', value: 'SELL' },
];

export const stockCategory: string[] = ['Breeding', 'Trade', 'Store', 'Stud'];

// TODO mock network data this will need to come from the API
export const networks = [
  {
    org: 'Elders',
    networks: [
      {
        id: 'AEF453',
        name: 'Sheep - WA',
      },
      {
        id: 'BEF454',
        name: 'Sheep - SA',
      },
    ],
  },
  {
    org: 'Agora',
    networks: [
      {
        id: 'CEF456',
        name: 'Sheep - WA',
      },
    ],
  },
];

export enum ListingVisibilityFilterEnum {
  Private = 'PRIVATE',
  Public = 'PUBLIC',
  Archived = 'ARCHIVED',
  Draft = 'HIDDEN',
}

export enum ListingPublicStatusFilterEnum {
  Review = 'REVIEW',
  Rejected = 'REJECTED',
  Approved = 'APPROVED',
}

export const addAllItem = (arr: { id: string; value: string }[]) => {
  const allItem = { id: 'all', value: 'All' };
  return [allItem, ...arr];
};

export const listingVisibilityFilterItems = addAllItem(
  Object.entries(ListingVisibilityFilterEnum).map(([label, value]) => ({
    id: value,
    value: label,
  }))
);

export const listingStatusItems = addAllItem(
  Object.entries(ListingPublicStatusFilterEnum).map(([label, value]) => ({
    id: value,
    value: label,
  }))
);

export interface IFilter {
  product?: ProductTypes;
  state?: string[];
  quantityMin?: string;
  quantityMax?: string;
  priceType?: string;
  priceMin?: string;
  priceMax?: string;
  search?: string;
  sold?: string[];
  type?: ListingTypeEnum;
  stockCat?: string[];
  networks?: string[];
  public_status?: string[];
  visibility?: ListingVisibilityFilterEnum;
  bookmark?: ListingBookmarkTypeEnum | null;
}

export function hasValue(value: string | undefined): boolean {
  return value !== undefined;
}

export const useBadgeCount = (stateFilter: IFilter) => {
  const badgeCount = useMemo(
    () => stateFilter && Object.keys(stateFilter).length,
    [stateFilter]
  );

  return badgeCount;
};

export const handleValueChange = (
  event: ChangeEvent<HTMLInputElement>,
  type: string,
  stateFilter: IFilter,
  setStateFilter: (filter: IFilter) => void
) => {
  const { name, value, checked } = event.target;
  if (type === inputType.INPUT) {
    let updatedFilter: IFilter = {
      ...stateFilter,
      [name]: value,
    };
    if (value === '' || value === undefined) {
      delete updatedFilter[name as keyof IFilter];
    }
    setStateFilter(updatedFilter);
  }

  if (type === inputType.RADIO) {
    let updatedFilter: IFilter = {
      ...stateFilter,
      [name]: value,
    };
    if (
      stateFilter[name as keyof IFilter] ===
      updatedFilter[name as keyof IFilter]
    ) {
      delete updatedFilter[name as keyof IFilter];
    }

    if (value === '' || value === undefined) {
      delete updatedFilter[name as keyof IFilter];
    }
    setStateFilter(updatedFilter);
  }

  if (type === inputType.CHECKBOX) {
    if (checked) {
      setStateFilter({
        ...stateFilter,
        [name]: Array.isArray(stateFilter[name as keyof IFilter])
          ? [...(stateFilter[name as keyof IFilter] as string[]), value]
          : [value],
      });
    } else {
      const updatedArray = (
        stateFilter[name as keyof IFilter] as string[]
      ).filter((item) => item !== value);

      const updatedFilter: IFilter = {
        ...stateFilter,
        [name]: updatedArray,
      };

      if (updatedArray.length === 0) {
        delete updatedFilter[name as keyof IFilter];
      }

      setStateFilter(updatedFilter);
    }
  }
};

export const handleSelectValueChange = (
  name: string,
  selected: any,
  type: string,
  stateFilter: IFilter,
  setStateFilter: (filter: IFilter) => void
) => {
  if (type === inputType.SELECT) {
    let updatedFilter: IFilter = {
      ...stateFilter,
      [name]: selected?.id,
    };
    if (updatedFilter[name as keyof IFilter] === null) {
      delete updatedFilter[name as keyof IFilter];
    }

    if (
      stateFilter[name as keyof IFilter] ===
      updatedFilter[name as keyof IFilter]
    ) {
      delete updatedFilter[name as keyof IFilter];
    }

    if (selected === '' || selected === undefined) {
      delete updatedFilter[name as keyof IFilter];
    }
    if (selected?.id === 'all') {
      delete updatedFilter[name as keyof IFilter];
    }
    setStateFilter(updatedFilter);
  }

  if (type === inputType.MULTISELECT) {
    const updatedFilter: IFilter = {
      ...stateFilter,
      [name as keyof IFilter]: selected,
    };

    Object.keys(updatedFilter).forEach((key) => {
      if (
        typeof updatedFilter[key as keyof IFilter] === 'string' &&
        (key === 'state' ||
          key === 'sold' ||
          key === 'stockCat' ||
          key === 'networks' ||
          key === 'public_status')
      ) {
        updatedFilter[key] = (
          updatedFilter[key as keyof IFilter] as string
        ).split(',');
      } else if (updatedFilter[key as keyof IFilter] === undefined) {
        delete updatedFilter[key as keyof IFilter];
      }
    });

    // Clear filter if 'all' is selected or no selection is made
    if (
      (selected?.length > 0 && selected.includes('all')) ||
      selected.length === 0
    ) {
      delete updatedFilter[name as keyof IFilter];
    }

    setStateFilter(updatedFilter);
  }
};

export function getListOfNetworks(data: INetworks): NetworkBasic[] {
  const allNetworks: NetworkBasic[] = [];
  data.networks.forEach((organization: OrgNetworks) => {
    organization.networks.forEach((network: NetworkBasic) => {
      allNetworks.push(network);
    });
  });
  return allNetworks;
}

export function getHeadNetworks(networks?: OrgNetworks[]): NetworkBasic[] {
  const allNetworks: NetworkBasic[] = [];
  if (!networks) return allNetworks;
  networks.forEach((organization: OrgNetworks) => {
    organization.networks.forEach((network: NetworkBasic) => {
      if (!network.org && network.head) {
        allNetworks.push(network);
      }
    });
  });
  return allNetworks;
}

export function getMyNetworks(
  networks?: OrgNetworks[],
  orgId?: string
): NetworkBasic[] {
  const allNetworks: NetworkBasic[] = [];
  if (!networks || !orgId) return allNetworks;

  networks?.forEach((organization: OrgNetworks) => {
    if (organization.id === orgId) {
      organization.networks.forEach((network: NetworkBasic) => {
        if (network.org) {
          allNetworks.push(network);
        }
      });
    }
  });
  return allNetworks;
}

export function getListOfNetworksId(networks: OrgNetworks[]): string[] {
  const allNetworks: string[] = [];
  if (!networks) return allNetworks;
  networks.forEach((organization: OrgNetworks) => {
    organization.networks.forEach((network: NetworkBasic) => {
      allNetworks.push(network.id);
    });
  });

  return allNetworks;
}
